/**
 * Import JSON (Documents view, editors): a JSON array, one object or NDJSON (one document per line, as mongoexport
 * writes it), Extended JSON welcome ({ "$oid" }, { "$date" }). Shows how many and the first ones, then inserts them in
 * one call: the ones that fail (a duplicate _id) do not stop the others, and the import can be undone for 10 minutes.
 */
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { UploadSimple } from '@phosphor-icons/react';
import { DataDialog, errorText } from './DataCommon';
import { importDocs } from '../../services/DataService';
import { formatCount } from '../../utils/dataModel';
import { JSON_MAX_DOCS, parseJsonDocs } from '../../utils/dataImport';

const PREVIEW = 3;
const FORMATS = { array: 'a JSON array', object: 'one JSON object', ndjson: 'one document per line (NDJSON)' };

const readText = (file) => (typeof file.text === 'function' ? file.text() : new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(reader.error);
  reader.readAsText(file);
}));

export default function JsonImportDialog({ projectId, collection, onImported, onClose }) {
  const [file, setFile] = useState(null); // { name, parsed }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const pickRef = useRef(null);

  const load = async (picked) => {
    if (!picked) { return; }
    setError(null);
    setResult(null);
    try {
      setFile({ name: picked.name, parsed: parseJsonDocs(await readText(picked)) });
    } catch (e) {
      setError(`Could not read the file: ${e?.message || e}`);
    }
  };

  const docs = file?.parsed?.ok ? file.parsed.docs : [];
  const tooMany = docs.length > JSON_MAX_DOCS;

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const answer = await importDocs(projectId, collection, docs);
      setResult(answer);
      if (answer?.inserted) { onImported?.(answer, file.name); }
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const failed = result?.failed || [];
  return (
    <DataDialog
      footer={(
        <>
          <span className="data-grow" />
          <button className="data-btn" onClick={onClose} type="button">{result ? 'Close' : 'Cancel'}</button>
          {!result && (
            <button className="data-btn is-primary" data-testid="json-import-run" disabled={busy || !docs.length || tooMany} onClick={run} type="button">
              {busy ? 'Importing…' : `Import ${formatCount(docs.length)} document${docs.length === 1 ? '' : 's'}`}
            </button>
          )}
        </>
      )}
      onClose={onClose}
      subtitle={<>Into <strong>{collection}</strong>. Documents without an <span className="data-mono">_id</span> get one.</>}
      testId="json-import"
      title={`Import JSON into ${collection}`}
      wide
    >
      <div className="data-row-gap is-wrap">
        <input accept=".json,.ndjson,.jsonl,application/json,application/x-ndjson" className="data-sr" data-testid="json-file" onChange={(e) => load(e.target.files?.[0])} ref={pickRef} type="file" />
        <button className="data-btn" data-autofocus disabled={busy} onClick={() => pickRef.current?.click()} type="button"><UploadSimple aria-hidden size={15} />{file ? 'Another file…' : 'Choose a JSON file…'}</button>
        {file && <span className="data-mono">{file.name}</span>}
      </div>
      {!file && <p className="data-footnote">A JSON array of documents, one document, or one per line (NDJSON). At most {formatCount(JSON_MAX_DOCS)} at a time.</p>}
      {file && !file.parsed.ok && <p className="data-error-text" role="alert">{file.parsed.error}</p>}
      {file?.parsed.ok && (
        <>
          <p data-testid="json-count">
            <strong>{formatCount(docs.length)} document{docs.length === 1 ? '' : 's'}</strong>, as {FORMATS[file.parsed.format]}.
            {tooMany ? ` That is more than ${formatCount(JSON_MAX_DOCS)}: split the file.` : ''}
          </p>
          <div className="data-label">The first {Math.min(PREVIEW, docs.length)}</div>
          <pre className="data-text-preview data-mono" data-testid="json-preview">{docs.slice(0, PREVIEW).map((d) => JSON.stringify(d, null, 2)).join('\n\n')}</pre>
        </>
      )}
      {result && (
        <div className={`data-check${failed.length ? ' is-bad' : ''}`} data-testid="json-result" role="status">
          <p>
            <strong>{formatCount(result.inserted)} inserted</strong>
            {result.failedCount ? `, ${formatCount(result.failedCount)} failed` : ''}.
            {result.inserted && result.changeId ? ' You can Undo the import for 10 minutes.' : ''}
          </p>
          {failed.length > 0 && (
            <ul aria-label="Documents that failed" className="data-check-list">
              {failed.slice(0, 20).map((f) => <li key={f.index}>Document {f.index + 1}: {f.error}</li>)}
              {result.failedCount > 20 && <li>… and {formatCount(result.failedCount - 20)} more</li>}
            </ul>
          )}
        </div>
      )}
      {error && <p className="data-error-text" role="alert">{error}</p>}
    </DataDialog>
  );
}

JsonImportDialog.propTypes = {
  collection: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onImported: PropTypes.func,
  projectId: PropTypes.string.isRequired,
};
